/**
 * GuestRoute Component
 *
 * A route guard for pages that should only be accessible to
 * unauthenticated users (like login, register pages).
 * Redirects authenticated users to their role-specific dashboard.
 *
 * Features:
 * - Redirects authenticated users based on role
 * - Preserves original destination if coming from protected route
 * - Shows loading state only during initial auth check (not during login operations)
 *
 * Note: We intentionally do NOT show loading spinner during login/logout operations
 * (when isLoading is true). This prevents unmounting the LoginPage, which would
 * cause it to lose error state when the login fails and the page remounts.
 */

import { type FC } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  useIsAuthenticated,
  useAuthInitialized,
  useUserRole,
} from '@/hooks/useAuth';
import { PageLoadingSpinner } from '@/components/common/LoadingSpinner';
import { useLanguage } from '@/hooks/useLanguage';
import { getRoleDashboard } from './RoleRoute';

interface GuestRouteProps {
  /** Optional fallback component while loading */
  fallback?: React.ReactNode;
}

interface LocationState {
  from?: {
    pathname: string;
  };
}

/**
 * GuestRoute guards routes meant for unauthenticated users only.
 * Authenticated users are redirected to their appropriate dashboard.
 *
 * @example
 * // Basic usage
 * <Route element={<GuestRoute />}>
 *   <Route path="login" element={<LoginPage />} />
 *   <Route path="register" element={<RegisterPage />} />
 * </Route>
 *
 * @example
 * // Redirect behavior
 * // If ADMIN tries to access /login → redirects to /admin
 * // If TEACHER tries to access /login → redirects to /teacher
 * // If STUDENT tries to access /login → redirects to /student
 */
export const GuestRoute: FC<GuestRouteProps> = ({ fallback }) => {
  const location = useLocation();
  const { t } = useLanguage();
  const isAuthenticated = useIsAuthenticated();
  const isInitialized = useAuthInitialized();
  const userRole = useUserRole();

  // Get the original destination if the user was redirected from a protected route
  const state = location.state as LocationState | null;
  const from = state?.from?.pathname;

  // Show loading state only during initial auth check (not during login/logout operations)
  // When isLoading is true during login, LoginPage should still render to show its own
  // loading state and preserve any error messages. Unmounting LoginPage during login
  // causes the component to lose its error state when remounted.
  if (!isInitialized) {
    return fallback ?? (
      <div className="min-h-screen flex items-center justify-center bg-surface-50">
        <PageLoadingSpinner
          variant="primary"
          label={t('common.loading')}
        />
      </div>
    );
  }

  // Redirect authenticated users to their dashboard or original destination
  if (isAuthenticated) {
    // If user was trying to access a protected route before being sent to login,
    // redirect them back there after authentication
    // Otherwise, send them to their role-specific dashboard
    const redirectTo = from ?? getRoleDashboard(userRole);

    return (
      <Navigate
        to={redirectTo}
        replace
      />
    );
  }

  // User is not authenticated - render guest routes (login, register, etc.)
  return <Outlet />;
};

export default GuestRoute;
